import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import useAssessmentCache from '@/hooks/useAssessmentCache';
import classes from './OpportunityPage.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ResourceAccessInstruction, {
  instructionIsInteractive,
  rendersInstruction
} from '@/components/Resources/ResourceAccessInstruction/ResourceAccessInstruction';
import t from '@/utils/Language/ResourceCard-translations';

// This card renders unconditionally: every opportunity is expected to have a way to connect,
// and the desktop layout permanently reserves the sidebar column for it, so returning null
// would leave a hole rather than reclaim the space.

const WhatToDoNextSection = ({ opportunity }) => {
  const router = useRouter();
  const { loadAssessment, isPublicForIntake } = useAssessmentCache();
  const accessInstructions = (opportunity?.access_instructions || []).filter(rendersInstruction);
  const intakeAssessmentId = opportunity?.intake_assessment?.assessment_id;

  // public_for_intake lives on the Mongo assessment record so load the assesment to determine
  // whether it is open to everyone and thus intended for display here.
  useEffect(() => {
    if (intakeAssessmentId) {
      loadAssessment(intakeAssessmentId);
    }
  }, [intakeAssessmentId, loadAssessment]);

  const showApplyDirectly = !!intakeAssessmentId && isPublicForIntake(intakeAssessmentId);

  // Clicking anywhere on a phone/email/link/location card behaves like clicking its link,
  // including the link's own tracking onClick.
  const handleInstructionCardClick = (event) => {
    if (event.target.closest('a')) return;
    // A location card lists one <address> per location, so scope to the clicked address
    // when there is one -- otherwise clicking beside the second address would open the
    // first address's map link. Other access types have no <address> and fall back to
    // the card's only link.
    const scope = event.target.closest('address') || event.currentTarget;
    const link = scope.querySelector('a');
    if (!link) return;
    if (scope === event.currentTarget) {
      link.click();
    } else {
      // Each address's tracking onClick lives on the div wrapping it, which this real
      // click has already bubbled through. Synthesizing a click would double-count it.
      window.open(link.href, '_blank', 'noreferrer');
    }
  };

  return (
    <div className={classes.whatToDoNextSection}>
      <div className={classes.whatToDoNextSectionPanel}>
        <div className={classes.whatToDoNextSectionHeader}>
          <FontAwesomeIcon icon={['far', 'circle-check']} className={classes.whatToDoNextSectionIcon} />
          <h2 className={classes.whatToDoNextSectionTitle}>{t('What to do next')}</h2>
        </div>
        <div className={classes.whatToDoNextSectionContent}>
          <p className={classes.waysToConnectLabel}>{t('Ways to connect')}</p>
          {showApplyDirectly && (
            <div
              className={`${classes.instructionItem} ${classes.applyDirectlyItem}`}
              onClick={() => router.push(`/assessments/${intakeAssessmentId}`)}
            >
              <span className={classes.applyDirectlyIconCircle}>
                <FontAwesomeIcon icon={['fas', 'check']} className={classes.applyDirectlyIcon} />
              </span>
              <div className={classes.instructionContent}>
                <p className={classes.applyDirectlyTitle}>{t('Apply directly through One Degree')}</p>
                <p className={classes.applyDirectlyNotes}>{t("We prefill this organization's intake form and send it for you.")}</p>
              </div>
              <FontAwesomeIcon icon={['fas', 'chevron-right']} className={classes.instructionCaret} />
            </div>
          )}
          {accessInstructions.map((instruction, index) => {
            // 'other' is free text, and a location instruction can arrive with no mappable
            // address -- both render as plain cards: no pointer, no hover, no caret, no click.
            const isStatic = !instructionIsInteractive(instruction);
            return (
              <div
                key={index}
                className={isStatic ? `${classes.instructionItem} ${classes.instructionItemStatic}` : classes.instructionItem}
                onClick={isStatic ? undefined : handleInstructionCardClick}
              >
                <div className={classes.instructionContent}>
                  <ResourceAccessInstruction instruction={instruction} resource={opportunity} addressBlockLayout="inline" showIconCircle />
                </div>
                {!isStatic && <FontAwesomeIcon icon={['fas', 'chevron-right']} className={classes.instructionCaret} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WhatToDoNextSection;
